import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Me, Requirement } from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import {
  LoadingState,
  ErrorState,
  EmptyState,
} from "../../components/QueryStates";
import { ShortId } from "../../components/ShortId";
import { AuditTrail } from "../audit/AuditTrail";

type StatusFilter = "open" | "fulfilled" | "cancelled" | "all";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "fulfilled", label: "Fulfilled" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

// The API caps `limit` at 100, so the panel loads one page and filters it in
// the browser.  When a tenant has more than that, the count line says so rather
// than quietly showing a filtered subset as if it were the whole picture.
const PAGE_LIMIT = 100;

function statusBadge(status: string): React.CSSProperties {
  const palette: Record<string, [string, string]> = {
    open: ["#fef3c7", "#b45309"],
    fulfilled: ["#dcfce7", "#15803d"],
    cancelled: ["#eef1f6", "#64748b"],
  };
  const [background, color] = palette[status] ?? ["#eef1f6", "#64748b"];
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 11px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.5,
    background,
    color,
  };
}

export function RequirementsPanel({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("open");
  const [msg, setMsg] = useState<string | null>(null);

  // Matches the backend gate on PATCH /requirements/{id}; hasRole() already
  // treats admin as passing.
  const canClose = hasRole(me, "buyer", "planner");
  // Same gate as the PO and receiving screens — /audit-events answers 403
  // otherwise, so showing the toggle to anyone else offers a dead button.
  const canViewAudit = hasRole(me, "finance", "manager");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["requirements"],
    queryFn: () => api.listRequirements(0, PAGE_LIMIT),
  });

  // Requirements carry part_id, not part_number — resolve it for display.
  const { data: partsData } = useQuery({
    queryKey: ["parts"],
    queryFn: api.listParts,
  });
  const partNumbers = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of partsData?.items ?? []) map.set(p.id, p.part_number);
    return map;
  }, [partsData]);

  const closeMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "fulfilled" | "cancelled";
    }) => api.closeRequirement(id, status),
    onSuccess: (req) => {
      setMsg(`✅ Requirement marked ${req.status}.`);
      void queryClient.invalidateQueries({ queryKey: ["requirements"] });
      // Closing demand changes what the consolidation scan can group.
      void queryClient.invalidateQueries({
        queryKey: ["consolidation-opportunities"],
      });
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const all = data?.items ?? [];
  const requirements =
    filter === "all" ? all : all.filter((r) => r.status === filter);

  const total = data?.total ?? 0;
  const truncated = total > all.length;

  const close = (r: Requirement, status: "fulfilled" | "cancelled") => {
    const pn = partNumbers.get(r.part_id) ?? "this requirement";
    const verb = status === "fulfilled" ? "Mark fulfilled" : "Cancel";
    if (window.confirm(`${verb}: ${pn} × ${r.quantity}?`))
      closeMutation.mutate({ id: r.id, status });
  };

  if (isLoading) return <LoadingState label="Loading requirements…" />;
  if (error) return <ErrorState error={error} />;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>Requirements ({total})</h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
      </div>

      <div
        style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}
      >
        {FILTERS.map(({ key, label }) => {
          const count =
            key === "all"
              ? all.length
              : all.filter((r) => r.status === key).length;
          const active = filter === key;
          return (
            <button
              key={key}
              aria-pressed={active}
              style={
                active
                  ? {
                      ...S.btnSm,
                      background: "#4f46e5",
                      color: "#fff",
                      border: "none",
                    }
                  : S.btnSm
              }
              onClick={() => setFilter(key)}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {msg && (
        <div style={{ ...S.card, background: "#e8f5e9", marginBottom: 12 }}>
          {msg}
        </div>
      )}

      {truncated && (
        <div style={{ ...S.card, marginBottom: 12 }}>
          <p style={S.muted}>
            Showing the {all.length} most recent of {total} requirements —
            filters apply to those only.
          </p>
        </div>
      )}

      {requirements.length === 0 ? (
        <EmptyState
          message={
            filter === "all"
              ? "No requirements yet — create one from New Requirement."
              : `No ${filter} requirements.`
          }
        />
      ) : (
        requirements.map((r) => (
          <div key={r.id} style={S.card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  {partNumbers.get(r.part_id) ?? "Unknown part"}
                </span>
                <span style={{ ...statusBadge(r.status), marginLeft: 10 }}>
                  {r.status}
                </span>
                {/* The only identity a requirement has. Four demands for the
                    same part, quantity and condition are otherwise identical
                    cards, and nothing on the PO screen could refer to one of
                    them. Full value on hover. */}
                <span style={{ marginLeft: 10 }}>
                  <ShortId id={r.id} prefix="REQ" />
                </span>
                {r.is_aog && (
                  <span
                    style={{
                      color: "#c0392b",
                      fontWeight: 700,
                      marginLeft: 10,
                    }}
                  >
                    ⚡ AOG
                  </span>
                )}
                <div style={{ ...S.muted, fontSize: 12, marginTop: 3 }}>
                  Qty{" "}
                  <span style={{ fontWeight: 600, color: "#333" }}>
                    {r.quantity}
                  </span>{" "}
                  · {r.condition_code}
                  {r.station ? ` · ${r.station}` : ""}
                  {r.work_order_ref ? ` · WO ${r.work_order_ref}` : ""}
                </div>
              </div>

              {canClose && r.status === "open" && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    style={S.btn}
                    onClick={() => close(r, "fulfilled")}
                    disabled={closeMutation.isPending}
                  >
                    Mark Fulfilled
                  </button>
                  <button
                    style={S.btnGhost}
                    onClick={() => close(r, "cancelled")}
                    disabled={closeMutation.isPending}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div
              style={{
                ...S.muted,
                marginTop: 6,
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: "4px 16px",
              }}
            >
              <span>Created: {new Date(r.created_at).toLocaleString()}</span>
              {/* Need-by is optional — an AOG demand raised over the phone often
                  has none. Omitted rather than shown as a dash, matching how
                  station and work order are handled above. */}
              {r.required_by_date && (
                <span>
                  Required by:{" "}
                  {new Date(r.required_by_date).toLocaleDateString()}
                </span>
              )}
            </div>
            {r.notes && (
              <p style={{ ...S.muted, marginTop: 6, fontStyle: "italic" }}>
                {r.notes}
              </p>
            )}
            {/* Where `FULFILLED_BY_RECEIPT` lives — the event that names the
                purchase order, the ordered quantity and what actually arrived,
                which together are the whole justification for a requirement
                being closed. Same finance/manager gate the API enforces. */}
            {canViewAudit && (
              <AuditTrail resourceType="requirement" resourceId={r.id} />
            )}
          </div>
        ))
      )}
    </div>
  );
}
